import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";

interface PurchaseRow {
  id: string;
  product_type: "video" | "kit";
  product_id: string;
  product_title: string;
  price: number;
  purchased_at: string;
  thumbnail_url: string | null;
  provider_id: string;
  status: string;
  provider_name?: string;
}

export default function PurchaseHistory() {
  const { currentUser } = useApp();
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.id) { setLoading(false); return; }
    const load = async () => {
      const { data } = await supabase
        .from("purchases")
        .select("*")
        .eq("buyer_id", currentUser.id)
        .order("purchased_at", { ascending: false });
      if (!data) { setLoading(false); return; }
      const ids = [...new Set(data.map((p) => p.provider_id))];
      const { data: profiles } = await supabase
        .from("profiles").select("id, name, service_name").in("id", ids);
      const map = Object.fromEntries((profiles || []).map((p) => [p.id, p.service_name || p.name]));
      setPurchases(data.map((p) => ({ ...p, provider_name: map[p.provider_id] ?? "事業者" })));
      setLoading(false);
    };
    load();
  }, [currentUser?.id]);

  if (loading) return <div className="flex justify-center py-10"><i className="ri-loader-4-line animate-spin text-2xl text-teal" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-serif text-deep-green text-lg">購入履歴</h3>
        <p className="text-xs text-charcoal/40 font-sans">全 {purchases.length} 件</p>
      </div>
      {purchases.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-stone-100">
          <i className="ri-shopping-bag-line text-5xl text-charcoal/20 block mb-4" />
          <p className="text-charcoal/40 font-sans text-sm mb-4">まだ購入履歴がありません</p>
          <Link to="/providers" className="bg-deep-green text-white px-6 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
            講座を探す →
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-100">
              <tr>
                <th className="text-left text-xs text-charcoal/50 font-sans font-medium px-5 py-3">商品</th>
                <th className="text-left text-xs text-charcoal/50 font-sans font-medium px-5 py-3 hidden md:table-cell">種別</th>
                <th className="text-left text-xs text-charcoal/50 font-sans font-medium px-5 py-3 hidden md:table-cell">購入日</th>
                <th className="text-right text-xs text-charcoal/50 font-sans font-medium px-5 py-3">金額</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p, i) => (
                <tr key={p.id} className={`${i < purchases.length - 1 ? "border-b border-stone-100" : ""} hover:bg-stone-50/50 transition-colors`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100">
                        {p.thumbnail_url && <img src={p.thumbnail_url} alt={p.product_title} className="w-full h-full object-cover object-top" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-sans text-charcoal truncate">{p.product_title}</p>
                        <p className="text-xs text-charcoal/40 font-sans">{p.provider_name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${p.product_type === "video" ? "bg-teal/10 text-teal" : "bg-gold/10 text-gold"}`}>
                      {p.product_type === "video" ? "動画講座" : "教材キット"}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <p className="text-xs text-charcoal/60 font-sans">{new Date(p.purchased_at).toLocaleDateString("ja-JP")}</p>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <p className="font-sans text-sm text-charcoal">¥{p.price.toLocaleString()}</p>
                    <span className={`text-xs font-sans ${p.status === "completed" ? "text-deep-green" : "text-charcoal/40"}`}>
                      {p.status === "completed" ? "完了" : "処理中"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
